import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { supabase, NEWS_TABLE, BLOG_TABLE } from '@/lib/supabase';
import type { NewsRecord, BlogRecord } from '@/lib/types';
import { uploadImage } from '@/lib/upload';
import MediaPicker from './MediaPicker';

interface EditorModalProps {
  mode: 'news' | 'blog';
  initial: NewsRecord | BlogRecord | null;
  onClose: () => void;
  onSaved: () => void;
}

const inputClass =
  'w-full px-4 py-2.5 rounded-xl bg-[#12121f] border border-[#2a2a3e] text-white placeholder:text-[#7a7a8a] focus:outline-none focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed]/40 transition-colors text-sm';

function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export default function EditorModal({ mode, initial, onClose, onSaved }: EditorModalProps) {
  const isEdit = !!initial;
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState('');
  const [content, setContent] = useState('');
  const [publishedAt, setPublishedAt] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [excerpt, setExcerpt] = useState('');
  const [author, setAuthor] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initial) {
      setTitle(initial.title);
      setSlug(initial.slug);
      setCategory(initial.category || '');
      setContent(initial.content || '');
      setPublishedAt(toDateInput(initial.published_at));
      setIsPublished(initial.is_published);
      if (mode === 'blog') {
        const b = initial as BlogRecord;
        setExcerpt(b.excerpt || '');
        setAuthor(b.author || '');
        setThumbnailUrl(b.thumbnail_url || '');
      }
    }
  }, [initial, mode]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError('');
    try {
      const url = await uploadImage(file);
      setThumbnailUrl(url);
    } catch {
      setError('画像のアップロードに失敗しました。');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    const publishedIso = publishedAt ? `${publishedAt}T00:00:00+09:00` : null;

    try {
      if (mode === 'news') {
        const payload = {
          title,
          slug,
          category,
          content,
          published_at: publishedIso,
          is_published: isPublished,
          updated_at: new Date().toISOString(),
        };
        const res = isEdit
          ? await supabase.from(NEWS_TABLE).update(payload).eq('id', (initial as NewsRecord).id)
          : await supabase.from(NEWS_TABLE).insert(payload);
        if (res.error) throw res.error;
      } else {
        const payload = {
          title,
          slug,
          category,
          content,
          excerpt,
          author,
          thumbnail_url: thumbnailUrl || null,
          published_at: publishedIso,
          is_published: isPublished,
          updated_at: new Date().toISOString(),
        };
        const res = isEdit
          ? await supabase.from(BLOG_TABLE).update(payload).eq('id', (initial as BlogRecord).id)
          : await supabase.from(BLOG_TABLE).insert(payload);
        if (res.error) throw res.error;
      }
      onSaved();
    } catch (err) {
      const msg = (err as { message?: string })?.message || '';
      setError(
        msg.includes('duplicate') || msg.includes('unique')
          ? 'このスラッグは既に使用されています。別のスラッグを指定してください。'
          : '保存に失敗しました。',
      );
    } finally {
      setSaving(false);
    }
  };

  const heading = isEdit
    ? mode === 'news'
      ? 'ニュースを編集'
      : 'ブログを編集'
    : mode === 'news'
      ? 'ニュースを新規作成'
      : 'ブログを新規作成';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-extrabold text-white">{heading}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-[#a0a0b0] hover:text-white hover:bg-[#2a2a3e] transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label htmlFor="editor-title" className="block text-sm font-medium text-white mb-2">
                タイトル <span className="text-red-400">*</span>
              </label>
              <input
                id="editor-title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={inputClass}
                placeholder="記事タイトル"
              />
            </div>
            <div>
              <label htmlFor="editor-slug" className="block text-sm font-medium text-white mb-2">
                スラッグ <span className="text-red-400">*</span>
              </label>
              <input
                id="editor-slug"
                required
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className={inputClass}
                placeholder="article-slug"
              />
            </div>
            <div>
              <label htmlFor="editor-category" className="block text-sm font-medium text-white mb-2">
                カテゴリ
              </label>
              <input
                id="editor-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={inputClass}
                placeholder="お知らせ / サービス など"
              />
            </div>
          </div>

          <div>
            <label htmlFor="editor-content" className="block text-sm font-medium text-white mb-2">
              本文 <span className="text-red-400">*</span>
            </label>
            <textarea
              id="editor-content"
              required
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              className={inputClass}
              placeholder="段落ごとに空行を入れると、公開ページで段落分けされます。"
            />
          </div>

          {mode === 'blog' && (
            <>
              <div>
                <label htmlFor="editor-excerpt" className="block text-sm font-medium text-white mb-2">
                  概要（抜粋）
                </label>
                <textarea
                  id="editor-excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  rows={2}
                  className={inputClass}
                  placeholder="一覧ページに表示される短い説明文"
                />
              </div>
              <div>
                <label htmlFor="editor-author" className="block text-sm font-medium text-white mb-2">
                  著者
                </label>
                <input
                  id="editor-author"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  className={inputClass}
                  placeholder="Luvira編集部"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">サムネイル画像</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    value={thumbnailUrl}
                    onChange={(e) => setThumbnailUrl(e.target.value)}
                    className={inputClass}
                    placeholder="画像URL"
                  />
                  <label className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-11 px-4 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer shrink-0">
                    {uploading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        アップロード中
                      </>
                    ) : (
                      <>
                        <i className="ri-upload-2-line"></i>
                        画像を選択
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload(f);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowPicker(true)}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-11 px-4 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer shrink-0"
                  >
                    <i className="ri-image-line"></i>
                    ライブラリから選択
                  </button>
                </div>
                {thumbnailUrl && (
                  <img src={thumbnailUrl} alt="サムネイル" className="mt-3 w-32 h-24 object-cover rounded-lg" />
                )}
              </div>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
            <div>
              <label htmlFor="editor-date" className="block text-sm font-medium text-white mb-2">
                公開日
              </label>
              <input
                id="editor-date"
                type="date"
                value={publishedAt}
                onChange={(e) => setPublishedAt(e.target.value)}
                className={inputClass}
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer px-4 py-2.5 rounded-xl bg-[#12121f] border border-[#2a2a3e]">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="w-4 h-4 accent-[#7c3aed] cursor-pointer"
              />
              <span className="text-sm text-white">公開する</span>
            </label>
          </div>

          {error && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">{error}</div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-12 px-6 text-sm font-semibold bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white transition-all duration-300 hover:brightness-110 disabled:opacity-60 cursor-pointer"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  保存中...
                </>
              ) : (
                '保存'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl h-12 px-6 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>

      {showPicker && (
        <MediaPicker
          onSelect={(url) => {
            setThumbnailUrl(url);
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}